<script setup>
import SlideOver from '@/components/SlideOver.vue';
import AppButton from '@/components/AppButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import { useForm } from '@inertiajs/vue3';
import FormError from '@/components/Forms/FormError.vue';
import { computed, ref, watch } from 'vue';
import AlertMessage from '@/components/AlertMessage.vue';
import DropdownButton from '@/components/DropdownButton.vue';
import { useDateFormat, useNow } from '@vueuse/core';
import axios from 'axios';
import BadgePill from '@/components/BadgePill.vue';
import FormRadioGroupCards from '@/components/Forms/FormRadioGroupCards.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';

const props = defineProps({
    settings: {
        type: Object,
        required: true,
    },
});

const open = ref(false);

const emit = defineEmits(['close']);
function dismiss() {
    open.value = false;
}

const form = useForm({
    mode: props.settings.defaults.mode,
    minutes: props.settings.defaults.minutes,
    student_ids: props.settings.defaults.student_ids,
    subject_id: '',
    tag_ids: [],
});

function selectAllStudents() {
    form.student_ids = props.settings.options.all_students_ids;
}

const contentBlocks = ref([]);

function handleSubjectChange(subjectId) {
    if (subjectId === '') {
        form.tag_ids = [];
        contentBlocks.value = [];
    } else {
        axios
            .get(props.settings.links.subject_tags_checkboxes, {
                params: { subject_id: subjectId },
            })
            .then((response) => {
                contentBlocks.value = response.data.items;
            });
    }
    console.log(subjectId);
}

watch(
    () => form.subject_id,
    (subjectId) => {
        handleSubjectChange(subjectId);
    },
);
const now = useNow();
const offsetDateTime = computed(() => {
    return new Date(now.value.getTime() + form.minutes * 60 * 1000);
});
const formatted = useDateFormat(offsetDateTime, 'HH:mm');

function submit() {
    form.post(props.settings.links.store, {
        preserveScroll: true,
        onSuccess: () => {
            emit('close');
            dismiss();
        },
    });
}

function deactivate() {
    form.delete(props.settings.links.delete, {
        preserveScroll: true,
        onSuccess: () => {
            emit('close');
            dismiss();
        },
    });
}
</script>

<template>
    <DropdownButton>
        <template v-slot:text>
            {{ $t('messages.practice_mode') }}
        </template>
        <template v-slot:content>
            <button
                type="button"
                @click="open = true"
                class="w-full text-left block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                style="display: none"
            >
                {{ $t('messages.activate') }} / {{ $t('messages.deactivate') }}
            </button>
            <InertiaLink
                :href="settings.links.group_practices_create"
                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
            >
                {{ $t('messages.activate') }}
            </InertiaLink>
            <InertiaLink
                :href="settings.links.tag_attempts"
                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
            >
                {{ $t('messages.results_per_tag') }}
            </InertiaLink>
            <InertiaLink
                :href="settings.links.subjects_tag_scores"
                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
            >
                {{ $t('messages.results_per_student') }}
            </InertiaLink>
            <InertiaLink
                :href="settings.links.students_tags_expected_levels"
                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
            >
                {{ $t('messages.students_tags_expected_levels') }}
            </InertiaLink>
        </template>
    </DropdownButton>
    <SlideOver size="xxl" :show="open" @close="dismiss()">
        <form action="" @submit.prevent="deactivate" v-if="settings.settings.is_active">
            <div class="font-semibold">
                <span>{{ settings.settings.mode_display }}</span>
            </div>

            <div class="whitespace-pre-line mt-5">
                {{ $t('messages.practice_mode_active_explanation', { mode: settings.settings.mode_display }) }}
            </div>
            <div class="flex justify-end p-2">
                <AppButton element="button" type="submit" desired-action>
                    {{ $t('messages.deactivate') }}
                </AppButton>
            </div>
        </form>
        <form action="" @submit.prevent="submit" v-else>
            <div>
                <div class="mt-1">
                    <FormLabel :value="$t('messages.activate_mode')" for="mode" />
                    <!--                    <FormSelect-->
                    <!--                        id="mode"-->
                    <!--                        v-model="form.mode"-->
                    <!--                        :options="settings.options.modes"-->
                    <!--                        class="mt-1 w-full"-->
                    <!--                        :null-option="false"-->
                    <!--                    />-->
                    <div>
                        <FormRadioGroupCards name="mode" v-model="form.mode" :options="settings.options.modes" />
                    </div>

                    <FormError :value="form.errors.mode" class="mt-2" />
                </div>
            </div>
            <div>
                <div class="mt-1">
                    <FormLabel :value="$t('messages.duration_of')" for="minutes">
                        <span class="text-gray-400">({{ $tc('messages.minutes', 2) }}) {{ formatted }}</span>
                    </FormLabel>

                    <FormInput
                        id="minutes"
                        v-model="form.minutes"
                        name="minutes"
                        type="number"
                        min="1"
                        max="400"
                        class="mt-1 w-full"
                        list="minutes"
                    />
                    <datalist id="minutes">
                        <option value="30"></option>
                        <option value="45"></option>
                        <option value="60"></option>
                    </datalist>
                    <FormError :value="form.errors.minutes" class="mt-2" />
                </div>
            </div>

            <div v-if="settings.options.subjects" class="mt-5">
                <div>
                    {{ $t('messages.(optional)') }}
                </div>
                <div>
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.subject')" for="subject_id" />
                        <FormSelect
                            id="mode"
                            v-model="form.subject_id"
                            :options="settings.options.subjects"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                    <div v-auto-animate>
                        <details v-for="block in contentBlocks" :key="block.id">
                            <summary>
                                {{ block.name }}
                            </summary>
                            <div v-for="section in block.sections" :key="section.name" class="mt-3">
                                <div>
                                    {{ section.name }}
                                </div>
                                <ul>
                                    <li v-for="tag in section.tags" :key="tag.id" class="mt-1">
                                        <label class="flex items-center gap-x-1">
                                            <input
                                                type="checkbox"
                                                name="tag_ids[]"
                                                :id="`tag_id_${tag.id}`"
                                                :value="tag.id"
                                                v-model="form.tag_ids"
                                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                            <BadgePill
                                                :color="form.tag_ids.includes(tag.id) ? 'indigo' : 'gray'"
                                                class="text-xs"
                                            >
                                                {{ tag.name }}
                                            </BadgePill>
                                        </label>
                                    </li>
                                </ul>
                            </div>
                        </details>
                    </div>
                </div>
            </div>

            <div class="mt-3">
                <div class="flex gap-2">
                    <div>
                        {{ $tc('messages.students', 2) }}
                    </div>
                    <div class="tabular-nums text-sm">
                        ({{ form.student_ids.length }} / {{ settings.options.students.length }})
                    </div>
                    <SelectAllNoneButtons
                        :max-count="settings.options.students.length"
                        :selected-count="form.student_ids.length"
                        @select-all="selectAllStudents()"
                        @select-none="form.student_ids = []"
                        class="inline-flex"
                    />
                </div>
                <div class="md:grid md:grid-cols-3 md:gap-6" v-auto-animate>
                    <div class="" v-for="student in settings.options.students" :key="student.value">
                        <label class="cursor-pointer">
                            <BadgePill :color="form.student_ids.includes(student.value) ? 'indigo' : 'gray'">
                                <input
                                    type="checkbox"
                                    class="sr-only"
                                    name="student_ids[]"
                                    :id="`student_ids_${student.value}`"
                                    :value="student.value"
                                    v-model="form.student_ids"
                                />
                                <span>{{ student.text }}</span>
                            </BadgePill>
                        </label>
                    </div>
                </div>
                <FormError :value="form.errors.student_ids" class="mt-2" />
            </div>

            <div class="mt-5">
                <AlertMessage class="mt-5 inline-block" type="info">
                    <template v-slot:title>
                        {{ $t('messages.information') }}
                    </template>
                    <template v-slot:body>
                        {{ $t('messages.practice_mode_explanation') }}
                    </template>
                </AlertMessage>
            </div>

            <div class="flex justify-end p-2">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </SlideOver>
</template>
